import React, { useEffect, useState, useCallback, useRef } from 'react';
import api, { authenticatedFetch } from '../../utils/axios';
import { Button, Text, Icon, Container, Select } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { FormPreviewPanelProps, FormOption } from './FormPreviewPanel.types';
import { PlatformType } from '../../types/project';

export const FormPreviewPanel: React.FC<FormPreviewPanelProps> = ({
  project,
  refreshTrigger = false,
  onClose,
  selectedFormKey: externalSelectedFormKey,
  onFormKeyChange
}) => {
  const [forms, setForms] = useState<FormOption[]>([]);
  const [internalSelectedFormKey, setInternalSelectedFormKey] = useState<string>('');
  const [formHtmlContent, setFormHtmlContent] = useState<string>('');
  const [loadingForms, setLoadingForms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const lastFetchedFormsKey = useRef<string>('');
  const lastFetchedPreviewKey = useRef<string>('');

  const projectId = project?._id;
  const isTapLinked = project?.tap_url !== null;

  const PLATFORM_LABELS: Partial<Record<PlatformType, string>> = {
    [PlatformType.ServiceNow]: 'ServiceNow',
    [PlatformType.PowerApps]: 'Power Apps',
    [PlatformType.Salesforce]: 'Salesforce',
  };

  const platformLabel = (project?.platform_type && PLATFORM_LABELS[project.platform_type]) ?? 'TAP';
  const agentLabel = `${platformLabel} Build Agent`;

  // Use external selectedFormKey if provided, otherwise use internal state
  const selectedFormKey = externalSelectedFormKey || internalSelectedFormKey;

  // Update internal state when external prop changes
  useEffect(() => {
    if (externalSelectedFormKey && externalSelectedFormKey !== internalSelectedFormKey) {
      console.log('📋 [FormPreviewPanel] Setting form key from prop:', externalSelectedFormKey);
      setInternalSelectedFormKey(externalSelectedFormKey);
    }
  }, [externalSelectedFormKey, internalSelectedFormKey]);

  const fetchForms = useCallback(async () => {
    if (!projectId || !isTapLinked) {
      return;
    }

    const fetchKey = `${projectId}-${isTapLinked}-${refreshTrigger}`;
    
    if (lastFetchedFormsKey.current === fetchKey) {
      return;
    }
    
    lastFetchedFormsKey.current = fetchKey;
    setLoadingForms(true);

    try {
      const response = await authenticatedFetch(`/api/projects/${projectId}/dataform-agent/forms`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch forms: ${response.status}`);
      }

      const data = await response.json();
      
      if (Array.isArray(data)) {
        setForms(data);
        if (data.length > 0 && !selectedFormKey) {
          const firstFormKey = data[0].business_key;
          setInternalSelectedFormKey(firstFormKey);
          onFormKeyChange?.(firstFormKey);
        }
      } else {
        setForms([]);
      }
    } catch (error) {
      console.error('Failed to fetch forms:', error);
      setForms([]);
    } finally {
      setLoadingForms(false);
    }
  }, [projectId, isTapLinked, refreshTrigger, selectedFormKey]);

  const fetchFormPreview = useCallback(async () => {
    if (!projectId || !selectedFormKey || !isTapLinked) {
      setFormHtmlContent('');
      return;
    }

    const fetchKey = `${projectId}-${selectedFormKey}-${refreshTrigger}`;
    
    if (lastFetchedPreviewKey.current === fetchKey) {
      return;
    }
    
    lastFetchedPreviewKey.current = fetchKey;
    setLoading(true);

    try {
      const response = await api.get(`/projects/${projectId}/dataform-agent/form-preview/${encodeURIComponent(selectedFormKey)}`);
      
      if (response.data.html_content) {
        setFormHtmlContent(response.data.html_content);
      } else {
        setFormHtmlContent('');
      }
    } catch (error) {
      console.error('Failed to fetch form preview:', error);
      setFormHtmlContent('');
    } finally {
      setLoading(false);
    }
  }, [projectId, selectedFormKey, isTapLinked, refreshTrigger]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  useEffect(() => {
    fetchFormPreview();
  }, [fetchFormPreview]);

  const handleRefreshPreview = () => {
    setIsRefreshing(true);
    lastFetchedPreviewKey.current = '';
    fetchFormPreview();
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const handleFormSelect = (value: string | number) => {
    const newKey = value.toString();
    setInternalSelectedFormKey(newKey);
    onFormKeyChange?.(newKey);
  };

  const handleOpenInNewTab = () => {
    if (!projectId || !selectedFormKey) return;
    const url = `/project/${projectId}/form?business_key=${encodeURIComponent(selectedFormKey)}`;
    window.open(url, '_blank');
  };

  const renderFormSelector = () => {
    if (!isTapLinked || forms.length === 0) return null;

    const formOptions = forms.map(form => ({
      label: form.title || form.name,
      value: form.business_key
    }));

    return (
      <div className="w-1/2 ml-auto">
        <Select
          options={formOptions}
          value={selectedFormKey}
          onChange={handleFormSelect}
          placeholder="Select a form"
          size="sm"
        />
      </div>
    );
  };

  const renderActionButtons = () => {
    if (!selectedFormKey || !formHtmlContent) return null;

    return (
      <div className="flex items-center gap-bg-v2-sm">
        <Button
          onClick={handleRefreshPreview}
          disabled={isRefreshing}
          variant='ghost'
          size='small'
          className="!p-bg-v2-md !rounded-bg-v2-md disabled:opacity-50"
          title="Refresh preview"
          label={<Icon name={IconName.Reload} size='sm' variant='secondary' spin={isRefreshing} />}
        />
        <Button
          onClick={handleOpenInNewTab}
          variant='ghost'
          size='small'
          className="!p-bg-v2-md !rounded-bg-v2-md"
          title="Open in new tab"
          label={<Icon name={IconName.ExternalLink} size='sm' variant='secondary' />}
        />
      </div>
    );
  };

  const renderHeader = () => {
    return (
      <div className='flex items-center justify-between min-h-[60px] max-h-[60px] px-bg-v2-xl bg-bg-primary border-b border-border-secondary'>
        <div className='flex items-center gap-bg-v2-lg flex-1'>
          <Icon name={IconName.Layout} size='sm' variant='secondary' />
          <Text variant='text-sm' weight='semibold' color='primary'>
            Form Preview
          </Text>
          {renderFormSelector()}
        </div>

        <div className="flex items-center gap-bg-v2-sm">
          {renderActionButtons()}
          {onClose && (
            <Button
              onClick={onClose}
              variant='ghost'
              size='small'
              className='!w-6 !h-6 !p-0 !rounded-full text-fg-secondary hover:text-fg-secondary-hover flex-shrink-0 ml-bg-v2-lg'
              aria-label='Close'
              label={<Icon name={IconName.Cross} size='sm' variant='secondary' />}
            />
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (!isTapLinked) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center max-w-md mx-auto p-bg-v2-2xl">
            <div className="text-text-tertiary mb-bg-v2-lg">
              <Icon name={IconName.Preview} size='xl' variant='secondary' />
            </div>
            <Text variant='text-lg' weight='medium' color='primary' className="mb-bg-v2-sm" align='center'>
              {platformLabel} Site Not Linked
            </Text>
            <Text variant='text-sm' color='secondary' align='center'>
              Please link your {platformLabel} site to view form previews.
            </Text>
          </div>
        </div>
      );
    }

    if (loadingForms) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <Icon name={IconName.Loading} size='xl' spin className="mx-auto mb-bg-v2-sm text-utility-brand-500" inheritColor />
            <Text variant='text-sm' color='secondary'>Loading forms...</Text>
          </div>
        </div>
      );
    }

    if (forms.length === 0) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center max-w-md mx-auto p-bg-v2-2xl">
            <div className="text-text-tertiary mb-bg-v2-lg">
              <Icon name={IconName.Preview} size='xl' variant='secondary' />
            </div>
            <Text variant='text-lg' weight='medium' color='primary' className="mb-bg-v2-sm" align='center'>
              No Forms Available
            </Text>
            <Text variant='text-sm' color='secondary' align='center'>
              Start chatting with the {agentLabel} to generate forms.
            </Text>
          </div>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <Icon name={IconName.Loading} size='xl' spin className="mx-auto mb-bg-v2-sm text-utility-brand-500" inheritColor />
            <Text variant='text-sm' color='secondary'>Loading form preview...</Text>
          </div>
        </div>
      );
    }

    if (!selectedFormKey) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center max-w-md mx-auto p-bg-v2-2xl">
            <div className="text-text-tertiary mb-bg-v2-lg">
              <Icon name={IconName.Preview} size='xl' variant='secondary' />
            </div>
            <Text variant='text-lg' weight='medium' color='primary' className="mb-bg-v2-sm" align='center'>
              No Form Selected
            </Text>
            <Text variant='text-sm' color='secondary' align='center'>
              Select a form from the dropdown to preview it.
            </Text>
          </div>
        </div>
      );
    }

    if (!formHtmlContent) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center max-w-md mx-auto p-bg-v2-2xl">
            <div className="text-text-tertiary mb-bg-v2-lg">
              <Icon name={IconName.Preview} size='xl' variant='secondary' />
            </div>
            <Text variant='text-lg' weight='medium' color='primary' className="mb-bg-v2-sm" align='center'>
              No Preview Available
            </Text>
            <Text variant='text-sm' color='secondary' align='center'>
              Unable to load form preview. Please try again.
            </Text>
          </div>
        </div>
      );
    }

    return (
      <iframe
        key={`${projectId}-${selectedFormKey}`}
        srcDoc={formHtmlContent}
        className="w-full h-full border-0"
        sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups allow-storage-access-by-user-activation"
        title="Form Preview"
      />
    );
  };

  return (
    <Container className='rounded-bg-v2-2xl shadow-bg-v2-md border border-border-secondary h-full flex flex-col overflow-hidden' padding='none'>
      {renderHeader()}
      <div className="flex-1 min-h-0 bg-bg-secondary overflow-hidden relative">
        {renderContent()}
      </div>
    </Container>
  );
};

export default FormPreviewPanel;
